import { create } from 'zustand';
import produce from 'immer';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { locationGetStringifiedSearchParams } from 'src/utils/location/get-stringified-search-params';
import { useCallback, useEffect, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import size from 'lodash/size';
import JsonURL from '@jsonurl/jsonurl';

export type Order = 'asc' | 'desc' | 'default';

export type Filters = Record<string, string[]>;

interface FilterBarParams {
  search_term: string;
  sort_by: string;
  sort_order: Order;
  page: number;
  filters: Filters;
}

function getFilterBarParamsFromLocation(searchStr = window.location.search): FilterBarParams {
  const {
    search_term = '',
    sort_order = 'default',
    sort_by = '',
    page = '1',
    filters = '',
  } = queryString.parse(searchStr) as Record<string, string>;

  let parsedFilters: Filters = {};
  try {
    if (filters.trim()) {
      parsedFilters = JsonURL.parse(filters);
    }
  } catch (e) {
    // No empty block
  }

  return {
    search_term,
    sort_order: sort_order as Order,
    sort_by,
    page: parseInt(page),
    filters: parsedFilters,
  };
}

interface State extends FilterBarParams {
  setSearchTerm: (search_term: string) => void;
  setSort: (sort_by: string, sort_order: Order) => void;
  setPage: (page: number) => void;
  setFilters: (filters: Filters) => void;
  setPartialParams: (values: Partial<FilterBarParams>) => void;
  reset: () => void;
}

const useFilterBarParamsStore = create<State>((set, get) => ({
  ...getFilterBarParamsFromLocation(),
  setSearchTerm: (search_term: string) => {
    set(
      produce((state) => {
        state.search_term = search_term;
        state.page = 1;
      })
    );
  },
  setSort: (sort_by: string, sort_order: Order) => {
    set(
      produce((state) => {
        state.sort_by = sort_by;
        state.sort_order = sort_order;
      })
    );
  },
  setPage: (page: number) => {
    set(
      produce((state) => {
        state.page = page;
      })
    );
  },
  setFilters: (filters: Filters) => {
    set(
      produce((state) => {
        state.filters = filters;
      })
    );
  },
  setPartialParams: (values: Partial<FilterBarParams>) => {
    set((state) => ({ ...state, ...values }));
  },
  reset: () => {
    set(() => ({
      search_term: '',
      sort_by: '',
      sort_order: 'default',
      page: 1,
      filters: {},
    }));
  },
}));

interface FilterParamParamsProps extends Partial<FilterBarParams> {
  dontDebounceSearch?: boolean;
}

export const useFilterBarParams = ({ dontDebounceSearch = false, ...defaultParams }: FilterParamParamsProps = {}): {
  filterBarParams: FilterBarParams;
  updateSearchTerm: ReturnType<typeof debounce>;
  updateSort: (newSortBy: string, newSortOrder: Order) => void;
  updatePage: (newPage: number) => void;
  updateFilters: (newfilters: Filters) => void;
  paramsReadyInternal: boolean;
} => {
  const history = useHistory();
  const location = useLocation();
  const filterBarParams = useFilterBarParamsStore();
  const [paramsDirty, setParamsDirty] = useState(false);
  const [paramsReadyInternal, setParamsReadyInternal] = useState(false);

  useEffect(() => {
    if (location.search) {
      filterBarParams.setPartialParams(getFilterBarParamsFromLocation(location.search));
    } else if (size(defaultParams)) {
      filterBarParams.setPartialParams(defaultParams);
    } else {
      filterBarParams.reset();
    }
    setParamsReadyInternal(false);
  }, [location]);

  useEffect(() => {
    if (!paramsReadyInternal) {
      setParamsReadyInternal(true);
    }
  }, [filterBarParams]);

  useEffect(() => {
    if (paramsDirty) {
      const urlSearchParams: Record<string, string> = {};

      if (filterBarParams.search_term) {
        urlSearchParams.search_term = filterBarParams.search_term;
      }

      if (filterBarParams.page > 1) {
        urlSearchParams.page = `${filterBarParams.page}`;
      }
      if (filterBarParams.sort_by) {
        urlSearchParams.sort_by = filterBarParams.sort_by;
      }
      if (filterBarParams.sort_order && filterBarParams.sort_order !== 'default') {
        urlSearchParams.sort_order = filterBarParams.sort_order;
      }
      if (filterBarParams.filters && Object.keys(filterBarParams.filters).length > 0) {
        urlSearchParams.filters = JsonURL.stringify(filterBarParams.filters) || '';
      }

      history.push({
        pathname: history.location.pathname,
        search: locationGetStringifiedSearchParams(history.location.search, urlSearchParams),
      });
      setParamsDirty(false);
    }
  }, [paramsDirty]);

  const updateSearchTerm = useCallback(
    debounce<(s: string) => void>(
      (searchTermInput) => {
        filterBarParams.setSearchTerm(searchTermInput);
        setParamsDirty(true);
      },
      dontDebounceSearch ? 0 : 500
    ),
    [dontDebounceSearch]
  );

  const updateSort = useCallback((sortBy, sortOrder) => {
    filterBarParams.setSort(sortBy, sortOrder);
    setParamsDirty(true);
  }, []);

  const updatePage = useCallback((page) => {
    filterBarParams.setPage(page);
    setParamsDirty(true);
  }, []);

  const updateFilters = useCallback((filters: Filters) => {
    filterBarParams.setFilters(filters);
    setParamsDirty(true);
  }, []);

  return {
    filterBarParams,
    updateSearchTerm,
    updateSort,
    updatePage,
    updateFilters,
    paramsReadyInternal,
  };
};
